/**
 * OffshoreLayout — OS-INV-04
 *
 * Chrome for protected OS pages. Mirrors MadebbLayout but the nav exposes ONLY
 * Inventory + Count Sheet (no Shop, Kanban, manufacturing, orders, etc.).
 * Sign out returns to the OS landing ('/os').
 *
 * Sets document.title to reflect Offshore Sails while an OS page is mounted and
 * restores the previous title on unmount, so Made.bb branding is not regressed.
 */

import React, { useEffect } from 'react';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  Inventory as InventoryIcon,
  Assignment as AssignmentIcon,
  Label as LabelIcon,
  FactCheck as AuditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface OffshoreLayoutProps {
  children: React.ReactNode;
  title: string;
}

export const OffshoreLayout: React.FC<OffshoreLayoutProps> = ({ children, title }) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  // OS-INV-04: brand the tab for Offshore Sails while an OS page is mounted,
  // restoring the prior title on unmount so Made.bb branding is unaffected.
  useEffect(() => {
    const previous = document.title;
    document.title = `Offshore Sails — ${title}`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      // ignore
    } finally {
      navigate('/os');
    }
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700}>
          {title}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Inventory">
            <IconButton
              onClick={() => navigate('/os/inventory')}
              size="small"
              data-testid="os-inventory-nav"
            >
              <InventoryIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Audit">
            <IconButton
              onClick={() => navigate('/os/audit')}
              size="small"
              data-testid="os-audit-nav"
            >
              <AuditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Count Sheet">
            <IconButton
              onClick={() => navigate('/os/count-sheet')}
              size="small"
              data-testid="os-count-sheet-nav"
            >
              <AssignmentIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Labels">
            <IconButton
              onClick={() => navigate('/os/labels')}
              size="small"
              data-testid="os-labels-nav"
            >
              <LabelIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" size="small" color="inherit" onClick={handleSignOut}>
            Sign out
          </Button>
        </Stack>
      </Stack>
      <Box>{children}</Box>
    </Box>
  );
};
